import { Alert, Box, FormControlLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import * as React from "react";
import { ISessionState } from "../../../models/session/sessionState";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as documentService from "../../../models/services/documentService";
import * as storeActions from "../../../models/store/storeActions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { DocumentTemplateList } from "../DocumentSettingsDialog/DocumentTemplateList";
import { IDocumentTemplateListItem } from "source/models/documentTemplate/documentTemplateListItem";
import { Base } from "ui/Scripts/source/framework/base";
import { PanelTitle } from "source/components/framework/Panel/PanelTitle";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

/* eslint-disable no-unused-vars */
const enum StartPageSelectionType {
    FromDocumentTemplate = "1",
    FromSharedDocumentTemplate = "2",
    AddNew = "3",
    AddNewShared = "4"
}

export const enum DocumentSaveAsTemplateDialogPage {
    Start = "1",
    SelectDocumentTemplate = "2",
    SelectSharedDocumentTemplate = "3",
    NewDocumentTemplateData = "4"
}
/* eslint-enable no-unused-vars */

export interface IDocumentSaveAsTemplateDialogProp {
    sessionState: ISessionState;
    companyId: string;
    content: string;
    landscapeOrientation: boolean;
    onClose: () => void;
}

export interface IDocumentSaveAsTemplateDialogState {
    selectionType: StartPageSelectionType;
    page: DocumentSaveAsTemplateDialogPage;
    id: string;
    companyId: string;
    code: string;
    comment: string;
}

export class DocumentSaveAsTemplateDialog extends React.Component<IDocumentSaveAsTemplateDialogProp, IDocumentSaveAsTemplateDialogState> {
    private orgStateHash: string = "";

    constructor(props: IDocumentSaveAsTemplateDialogProp) {
        super(props);
        this.state = {
            selectionType: StartPageSelectionType.AddNew,
            page: DocumentSaveAsTemplateDialogPage.Start,
            id: "",
            companyId: "",
            code: "",
            comment: ""
        };
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    getSaveDataFromState = async (props: IDocumentSaveAsTemplateDialogProp, state: IDocumentSaveAsTemplateDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        const selectionWasShared = state.selectionType === StartPageSelectionType.FromSharedDocumentTemplate || state.selectionType === StartPageSelectionType.AddNewShared;
        const selectionWasNew = state.selectionType === StartPageSelectionType.AddNew || state.selectionType === StartPageSelectionType.AddNewShared;
        result.append("id", selectionWasNew ? Base.emptyGuid : state.id);
        result.append("companyId", selectionWasShared ? null : props.companyId);
        result.append("rowId", null);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("content", props.content);
        result.append("landscapeOrientation", props.landscapeOrientation);
        return result;
    };

    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        return await AppUtils.callService(() => documentService.saveDocumentTemplateEdit(saveData.obj));
    };

    handleSave = async () => {
        const result = await this.saveToDb();
        if (!result) return;
        this.props.onClose();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave();
        } else {
            props.onClose();
        }
    };

    handleMoveToPreviousPage = () => {
        const state = this.state;
        if (state.page === DocumentSaveAsTemplateDialogPage.Start) return;
        const selectionWasNew = state.selectionType === StartPageSelectionType.AddNew || state.selectionType === StartPageSelectionType.AddNewShared;
        const page = state.page === DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate ||
            state.page === DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate ||
            selectionWasNew && state.page === DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData
            ? DocumentSaveAsTemplateDialogPage.Start
            : (state.companyId
                ? DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate
                : DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate);
        const newPageIsStart = page === DocumentSaveAsTemplateDialogPage.Start;
        this.setState({
            page,
            id: newPageIsStart ? "" : state.id,
            companyId: newPageIsStart ? "" : state.companyId,
            code: newPageIsStart ? "" : state.code,
            comment: newPageIsStart ? "" : state.comment
        });
    };
    
    handleMoveToNextPage = async () => {
        const state = this.state;
        if (state.page === DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData) return;
        const page = state.page === DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate || state.page === DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate
            ? DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData
            : (state.selectionType === StartPageSelectionType.FromDocumentTemplate
                ? DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate
                : (state.selectionType === StartPageSelectionType.FromSharedDocumentTemplate
                    ? DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate
                    : DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData
                ));
        this.setState({
            page
        });
    };

    handleChangeStartPageSelectionType = (checked: boolean, selectionType: StartPageSelectionType) => {
        if (!checked) return;
        this.setState({
            selectionType
        });
    };

    handleSelectDocumentTemplate = (item: IDocumentTemplateListItem) => {
        this.setState({
            id: item.id,
            companyId: item.companyId,
            code: item.code,
            comment: item.comment
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isLastPage = state.page === DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData;
        const selectionWasShared = state.selectionType === StartPageSelectionType.FromSharedDocumentTemplate || state.selectionType === StartPageSelectionType.AddNewShared;
        const selectionWasNew = state.selectionType === StartPageSelectionType.AddNew || state.selectionType === StartPageSelectionType.AddNewShared;
        return (
            <Dialog
                id="saveDocumentAsTemplateDialog"
                maxWidth="sm"
                maxHeight="768px"
                title={Translations.SaveToTemplate}
                body={
                    <>
                        {state.page === DocumentSaveAsTemplateDialogPage.Start &&
                            <GridRow>
                                <Grid item xs>
                                    <FormField title={null} margin={0}>
                                        <RadioGroup value={state.selectionType} onChange={(_e, value) => this.handleChangeStartPageSelectionType(true, value as StartPageSelectionType)}>
                                            <FormControlLabel
                                                label={Translations.AddDocumentTemplate}
                                                value={StartPageSelectionType.AddNew}
                                                control={
                                                    <Radio />
                                                }
                                            />
                                            {props.sessionState.isSuperAdmin &&
                                                <FormControlLabel
                                                    label={Translations.AddSharedDocumentTemplate}
                                                    value={StartPageSelectionType.AddNewShared}
                                                    control={
                                                        <Radio />
                                                    }
                                                />
                                            }
                                            <FormControlLabel
                                                label={Translations.ModifyExistingDocumentTemplate}
                                                value={StartPageSelectionType.FromDocumentTemplate}
                                                control={
                                                    <Radio />
                                                }
                                            />
                                            {props.sessionState.isSuperAdmin &&
                                                <FormControlLabel
                                                    label={Translations.ModifyExistingSharedDocumentTemplate}
                                                    value={StartPageSelectionType.FromSharedDocumentTemplate}
                                                    control={
                                                        <Radio />
                                                    }
                                                />
                                            }
                                        </RadioGroup>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.page === DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate &&
                            <VerticalContainer
                                before={<Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.SelectDocumentTemplate}</Alert>}
                                main={
                                    <DocumentTemplateList
                                        companyId={props.companyId}
                                        height="100%"
                                        hideInfo={true}
                                        selectMode={true}
                                        selectedId={state.id}
                                        onSetSelected={this.handleSelectDocumentTemplate}
                                    />
                                }
                            />
                        }
                        {state.page === DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate &&
                            <VerticalContainer
                                before={<Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.SelectDocumentTemplate}</Alert>}
                                main={
                                    <DocumentTemplateList
                                        companyId={null}
                                        height="100%"
                                        selectMode={true}
                                        selectedId={state.id}
                                        onSetSelected={this.handleSelectDocumentTemplate}
                                    />
                                }
                            />
                        }
                        {state.page === DocumentSaveAsTemplateDialogPage.NewDocumentTemplateData &&
                            <Box display="flex" flexDirection="column">
                                <PanelTitle title={(selectionWasShared ? Translations.SharedDocumentTemplate : Translations.DocumentTemplate) + ": " + (selectionWasNew ? Translations.ANew : state.code)} />
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Name} required>
                                            <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Description} fullWidth>
                                            <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </Box>
                        }
                    </>
                }
                buttons={[
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.Previous, variant: "outlined", onClick: state.page === DocumentSaveAsTemplateDialogPage.Start ? null : this.handleMoveToPreviousPage },
                    {
                        title: !isLastPage ? Translations.Next : Translations.Save,
                        variant: "confirmation",
                        onClick: !isLastPage ? this.handleMoveToNextPage : this.handleSave,
                        disabled: (state.page === DocumentSaveAsTemplateDialogPage.SelectDocumentTemplate || state.page === DocumentSaveAsTemplateDialogPage.SelectSharedDocumentTemplate) && !state.id
                    }
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}